import React, { useState, ReactElement, FunctionComponent } from 'react'
import { ICompany, PurchaseOrderReceiptNotFound, API } from '@getgreenline/homi-shared'
import { Button, List, Icon } from 'antd'
import { uploadImageAndGetCloudinaryUrl } from '../../../../../../../utilities/helpers'
import { Collapse } from 'reactstrap'
import { Track } from '../../../../../../../Track'
import { CreateProduct } from '../../../../../DashboardProducts/AddProduct/ProductStore'
import { CLOUDINARY } from '../../../../../../../constants/general'
import { CurrentCompanyStore } from '../../../../../../../stores/CurrentCompanyStore'
import { isNil } from 'lodash'

interface ICreateProductNotFound extends PurchaseOrderReceiptNotFound {
  imageUrl?: string
  description?: string
  barcode?: string
  cannabisWeight?: number
  weight?: number
  cbd?: number
  thc?: number
  minCbd?: number
  maxCbd?: number
  minThc?: number
  maxThc?: number
}

interface Props {
  company: ICompany
  foundInCatalogue: PurchaseOrderReceiptNotFound[]
  notFound: PurchaseOrderReceiptNotFound[]
  refreshReceiptProducts: () => void
  isRefreshing: boolean
  currentCompanyStore: CurrentCompanyStore
}

// This component is only for admin users for now
export const POCatalogueProductsAdmin: FunctionComponent<Props> = (props: Props) => {
  const { company, foundInCatalogue, notFound, refreshReceiptProducts, isRefreshing } = props
  const [isExpanded, toggleExpanded] = useState(false)
  const [isCreatingProducts, setIsCreatingProducts] = useState(false)
  const totalNotFound = foundInCatalogue.length + notFound.length

  const onCreateProducts = async () => {
    setIsCreatingProducts(true)

    await Promise.all(
      [...foundInCatalogue, ...notFound].map(
        async (csvProduct: ICreateProductNotFound): Promise<void> => {
          const product = new CreateProduct()

          if (csvProduct.imageUrl && !csvProduct.imageUrl.includes(CLOUDINARY)) {
            try {
              const cloudinaryUrl = await uploadImageAndGetCloudinaryUrl(
                csvProduct.imageUrl,
                props.currentCompanyStore!,
              )
              product.setImageUrl(cloudinaryUrl)
            } catch (error) {
              product.setImageUrl(null)
            }
          } else {
            product.setImageUrl(csvProduct.imageUrl || null)
          }
          product.setInventoryMode(true)
          product.setName(csvProduct.name)
          product.setSKU(csvProduct.sku)
          isNil(csvProduct.barcode)
            ? product.generateBarcode()
            : product.setBarcode(csvProduct.barcode)
          if (!isNil(csvProduct.description)) {
            product.setDescription(csvProduct.description)
          }
          if (!isNil(csvProduct.cannabisWeight)) {
            product.setCannabisWeight(csvProduct.cannabisWeight)
          }
          if (!isNil(csvProduct.weight)) {
            product.setWeight(csvProduct.weight)
          }

          const productKeyMaps = {
            cbd: 'cbd',
            thc: 'thc',
            minCbd: 'minCBD',
            maxCbd: 'maxCBD',
            minThc: 'minTHC',
            maxThc: 'maxTHC',
          }

          Object.keys(productKeyMaps).forEach((key) => {
            if (!isNil(csvProduct[key])) {
              product.metaData.setValue(productKeyMaps[key], csvProduct[key])
            }
          })

          try {
            const createdProduct = await API.addProduct(company.id, product.networkObject)
          } catch (error) {
            console.error(error)
          }
        },
      ),
    )

    await refreshReceiptProducts()
    setIsCreatingProducts(false)
  }

  return (
    <section className='product-list-wrapper'>
      <header className='alert alert-warning mb-0'>
        <div
          role='button'
          className='d-flex justify-content-between align-items-center cursor-pointer'
          onClick={() => {
            toggleExpanded(!isExpanded)
            Track.track('dashboard:purchaseOrder_importProductsNotFoundClicked')
          }}
          onKeyDown={() => {
            toggleExpanded(!isExpanded)
            Track.track('dashboard:purchaseOrder_importProductsNotFoundClicked')
          }}
          tabIndex={0}
        >
          <span>{`${totalNotFound} products not found`}</span>

          <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} aria-hidden='true' />
        </div>

        <Collapse isOpen={isExpanded}>
          <div className='mt-3'>
            <span>
              Products that were not found can be added to the Product List by clicking the button
              below.
            </span>

            <div className='mt-2'>
              <Button
                onClick={onCreateProducts}
                onKeyDown={onCreateProducts}
                disabled={isCreatingProducts || isRefreshing}
              >
                Add All
              </Button>
              {(isCreatingProducts || isRefreshing) && (
                <Icon type='sync' spin={true} className='mx-2 align-self-center' />
              )}
            </div>
          </div>
        </Collapse>
      </header>

      <Collapse isOpen={isExpanded}>
        <div style={{ maxHeight: 300, overflowY: 'scroll' }}>
          <h6 className='mt-3'>Products Not Found</h6>
          <hr className='mb-3'></hr>
          <List
            itemLayout='horizontal'
            dataSource={[...foundInCatalogue, ...notFound]}
            renderItem={(item) => (
              <MissingPOProduct key={item.sku} name={item.name} sku={item.sku} company={company} />
            )}
          />
        </div>
      </Collapse>
    </section>
  )
}

interface IMissingPOSProductsProps extends PurchaseOrderReceiptNotFound {
  company: ICompany
}

const MissingPOProduct = ({ name, sku, company }: IMissingPOSProductsProps) => {
  return (
    <li className='row ml-0 mr-0 mb-2'>
      <main className='col-12 p-0 product-list__main w-100'>
        <h2 className='product-name'>
          <span>{name}</span>
        </h2>

        <small className='product-list__description'>
          <span>
            {`SKU: ${sku || 'Empty SKU'} ${company.province === 'AB' ? `| category` : ''}`}
          </span>
        </small>
        <hr></hr>
      </main>
    </li>
  )
}
